
import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  InheritAttributes,
  Condition,
  SENSES,
  Skill,
  SKILLS,
  Tool,
} from "@dndModels";
import { 
  DAMAGE_TYPES,
  TRUE_DAMAGE_TYPES,
} from "@dndModels/Constants";
import { AbilityScoreType, DamageType } from "@dndModels/Types";

import DandDAPI from "@dndUtilities/system_api";

import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import LoadingBox from "@components/input/LoadingBox";
import CommonUtilities from '@utilities/common_utilities';
import FlexBox from '@components/layout/FlexBox';

interface Props {
  attributes: InheritAttributes;
  disabled: boolean;
  onChange: () => void;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function InheritAttributesInput({
  attributes,
  disabled,
  onChange
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [edit_attribute, setEditAttribute] = useState("");
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);

  const load = useCallback( async () => {
    setLoading(true);
    const res = await api.getSetOfObjects({ data_types: ["condition","tool"] });
    if (res) {
      setConditions(res.condition as Condition[]);
      setTools(res.tool as Tool[]);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if(reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderSectionHeader = (header: string) => {
    return (
      <>
        <Grid size={2} style={{ cursor: "pointer", fontWeight: "bold" }}
          onClick={() => {
            setEditAttribute("");
          }}>
          Back
        </Grid>
        <Grid size={10}>
          { header }
        </Grid>
      </>
    );
  }

  const renderSpeed = (use_entry: InheritAttributes, name: string, display: string) => {
    return (
      <Grid
        key={name}
        size={{
          xs: 4
        }}>
        <CheckBox
          name={ display }
          value={ use_entry.speeds.includes(name) }
          disabled={disabled}
          onChange={(changed) => {
            if (changed) {
              use_entry.speeds.push(name);
            } else {
              use_entry.speeds = use_entry.speeds.filter(o => o !== name);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderSpeeds = () => {
    const use_entry = attributes;
    return (
      <Grid container spacing={0} direction="row">
        { renderSectionHeader("Speeds") }
        <Grid size={12}>
          <ToggleButtonBox
            name="All"
            value={ use_entry.all_speeds }
            disabled={disabled}
            onToggle={() => {
              if (use_entry.all_speeds) {
                use_entry.speeds = [];
                use_entry.hover = false;
              } else {
                use_entry.hover = true;
                use_entry.speeds = ["walk","swim","climb","fly","burrow"];
              }
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
        {renderSpeed(use_entry, "walk", "Walk")}
        {renderSpeed(use_entry, "swim", "Swim")}
        {renderSpeed(use_entry, "climb", "Climb")}
        {renderSpeed(use_entry, "fly", "Fly")}
        {renderSpeed(use_entry, "burrow", "Burrow")}
        <Grid size={4}>
          <ToggleButtonBox
            name="Hover"
            value={ use_entry.hover }
            disabled={disabled}
            onToggle={() => {
              // const new_obj = obj;
              use_entry.hover = !use_entry.hover;
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const renderSense = (use_entry: InheritAttributes, name: string) => {
    return (
      <Grid
        key={name}
        size={{
          xs: 4
        }}>
        <CheckBox
          name={ name }
          value={ use_entry.senses.includes(name) }
          disabled={disabled}
          onChange={(changed) => {
            if (changed) {
              use_entry.senses.push(name);
            } else {
              use_entry.senses = use_entry.senses.filter(o => o !== name);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderSenses = () => {
    const use_entry = attributes;
    return (
      <Grid container spacing={0} direction="row">
        { renderSectionHeader("Senses") }
        <Grid size={12}>
          <ToggleButtonBox
            name="All"
            value={ use_entry.all_senses }
            disabled={disabled}
            onToggle={() => {
              if (use_entry.all_senses) {
                use_entry.senses = [];
              } else {
                use_entry.senses = SENSES.map(o => o.name);
              }
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
        { SENSES.map(sense => {
          return renderSense(use_entry, sense.name); 
        })}
      </Grid>
    );
  }
  
  const renderSkill = (use_entry: InheritAttributes, skill: Skill) => {
    // if (skill.name === "Arcana") {
    //   // console.log(skill);
    //   // console.log(obj.skill_proficiencies2[skill._id]);
    // }
    return (
      <Grid
        key={skill._id}
        size={{
          xs: 4
        }}>
        <CheckBox
          name={ skill.name }
          value={ use_entry.skill_profs.includes(skill._id) }
          disabled={disabled}
          onChange={(changed) => {
            if (changed) {
              use_entry.skill_profs.push(skill._id);
            } else {
              use_entry.skill_profs = use_entry.skill_profs.filter(o => o !== skill._id);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderSkills = () => {
    return (
      <Grid container spacing={0} direction="row">
        { renderSectionHeader("Skills") }
        <Grid size={12}>
          <ToggleButtonBox
            name="All"
            value={ attributes.all_skills }
            disabled={disabled}
            onToggle={() => {
              if (attributes.all_skills) {
                attributes.skill_profs = [];
              } else {
                attributes.skill_profs = SKILLS.map(o => o._id);
              }
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
        { SKILLS.sort((a,b) => a.name.localeCompare(b.name)).map(skill => {
          return renderSkill(attributes, skill);
        })}
      </Grid>
    );
  }
  
  const renderSavingThrow = (use_entry: InheritAttributes, ability_score: AbilityScoreType) => {
    return (
      <Grid size={4}>
        <CheckBox
          name={ ability_score }
          value={ use_entry.save_profs.includes(ability_score) }
          disabled={disabled}
          onChange={(changed: boolean) => {
            // const new_obj = obj;
            if (changed) {
              use_entry.save_profs.push(ability_score);
            } else {
              use_entry.save_profs = use_entry.save_profs.filter(o => o !== ability_score);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderSavingThrows = () => {
    return (
      <Grid container spacing={0} direction="row">
        { renderSectionHeader("Saving Throws") }
        <Grid size={12}>
          <ToggleButtonBox
            name="All"
            value={ common_util.listsMatch(attributes.save_profs, ["STR","DEX","CON","INT","WIS","CHA"]) }
            disabled={disabled}
            onToggle={() => {
              if (common_util.listsMatch(attributes.save_profs, ["STR","DEX","CON","INT","WIS","CHA"])) {
                attributes.save_profs = [];
              } else {
                attributes.save_profs = ["STR","DEX","CON","INT","WIS","CHA"];
              }
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
        { renderSavingThrow(attributes, "STR") }
        { renderSavingThrow(attributes, "DEX") }
        { renderSavingThrow(attributes, "CON") }
        { renderSavingThrow(attributes, "INT") }
        { renderSavingThrow(attributes, "WIS") }
        { renderSavingThrow(attributes, "CHA") }
      </Grid>
    );
  }
  
  const renderTool = (use_entry: InheritAttributes, tool: Tool) => {
    return (
      <Grid
        key={tool._id}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ tool.name }
          disabled={disabled}
          value={ use_entry.tool_profs.includes(tool.name) }
          onToggle={() => {
            // const new_obj = obj;
            if (use_entry.tool_profs.includes(tool.name)) {
              use_entry.tool_profs = use_entry.tool_profs.filter(o => o !== tool.name);
            } else {
              use_entry.tool_profs.push(tool.name);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderTools = () => {
    return (
      <Grid container spacing={0} direction="row">
        { renderSectionHeader("Tools") }
        <Grid size={12}>
          <ToggleButtonBox
            name="All"
            value={ common_util.listsMatch(attributes.tool_profs, tools.map(o => o.name)) }
            disabled={disabled}
            onToggle={() => {
              if (common_util.listsMatch(attributes.tool_profs, tools.map(o => o.name))) {
                attributes.tool_profs = [];
              } else {
                attributes.tool_profs = tools.map(o => o.name);
              }
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
        { tools && tools.map(tool => {
          return renderTool(attributes, tool);
        })}
      </Grid>
    );
  }
  
  const renderCondition = (use_entry: InheritAttributes, condition: Condition) => {
    return (
      <Grid
        key={condition._id}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ condition.name }
          disabled={disabled}
          value={ use_entry.condition_immunities.includes(condition.name) }
          onToggle={() => {
            // const new_obj = obj;
            if (use_entry.condition_immunities.includes(condition.name)) {
              use_entry.condition_immunities = use_entry.condition_immunities.filter(o => o !== condition.name);
            } else {
              use_entry.condition_immunities.push(condition.name);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderConditionImmunities = () => {
    const common_conditions = conditions.filter(o => o.common);
    return (
      <Grid container spacing={0} direction="row">
        { renderSectionHeader("Condition Immunities")}
        <Grid size={12}>
          <ToggleButtonBox
            name="All"
            value={ common_util.listsMatch(attributes.condition_immunities, common_conditions.map(o => o.name)) }
            disabled={disabled}
            onToggle={() => {
              if (common_util.listsMatch(attributes.condition_immunities, common_conditions.map(o => o.name))) {
                attributes.condition_immunities = [];
              } else {
                attributes.condition_immunities = common_conditions.map(o => o.name);
              }
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
        { common_conditions.map(condition => {
          return renderCondition(attributes, condition);
        })}
      </Grid>
    );
  }
  
  const renderDamageType = (use_entry: InheritAttributes, damage_type: DamageType, multiplier: number) => {
    const damage_multipliers = multiplier === 0 ? use_entry.damage_immunities : (multiplier === 0.5 ? use_entry.damage_resistances : use_entry.damage_vulnerabilities);
    return (
      <Grid
        key={damage_type}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ damage_type }
          disabled={disabled}
          value={ damage_multipliers.includes(damage_type) }
          onToggle={() => {
            // const new_obj = obj;
            let damage_types = multiplier === 0 ? use_entry.damage_immunities : (multiplier === 0.5 ? use_entry.damage_resistances : use_entry.damage_vulnerabilities);
            if (damage_types.includes(damage_type)) {
              damage_types = damage_types.filter(o => o !== damage_type);
            } else {
              damage_types.push(damage_type);
            }
            if (multiplier === 0) {
              use_entry.damage_immunities = damage_types;
            } else if (multiplier === 0.5) {
              use_entry.damage_resistances = damage_types;
            } else {
              use_entry.damage_vulnerabilities = damage_types;
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderDamageTypes = (use_entry: InheritAttributes, multiplier: number) => {
    const plural = multiplier === 0 ? "Immunities" : (multiplier === 0.5 ? "Resistances" : "Vulnerabilities");
    return (
      <Grid container spacing={0} direction="row">
        { renderSectionHeader(`Damage ${plural}`) }
        <Grid size={12}>
          <ToggleButtonBox
            name="All"
            value={ use_entry.allDamageMultiplier(multiplier) }
            disabled={disabled}
            onToggle={() => {
              if (use_entry.allDamageMultiplier(multiplier)) {
                use_entry.allDamageMultiplier(multiplier, false);
              } else {
                use_entry.allDamageMultiplier(multiplier, true)
              }
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
        { TRUE_DAMAGE_TYPES.map(damage_type => {
          return renderDamageType(use_entry, damage_type, multiplier);
        })}
        <Grid size={12}>
          <CheckBox
            name={`Extra Damage ${plural}`}
            value={ multiplier === 0 ? use_entry.extra_damage_immunities : (multiplier === 0.5 ? use_entry.extra_damage_resistances : use_entry.extra_damage_vulnerabilities) }
            disabled={disabled}
            onChange={(changed) => {
              if (multiplier === 0) {
                use_entry.extra_damage_immunities = changed;
              } else if (multiplier === 0.5) {
                use_entry.extra_damage_resistances = changed;
              } else {
                use_entry.extra_damage_vulnerabilities = changed;
              }
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const renderLanguages = () => {
    return (
      <Grid container spacing={0} direction="row">
        { renderSectionHeader(`Languages`) }
        <Grid size={12}>
          {/* <StringBox
            name="Languages"
            value={attributes.languages}
            disabled={disabled}
            onBlur={(changed: string) => {
              // const new_obj = obj;
              attributes.languages = changed;
              onChange();
              setPing(!ping);
            }}
          /> */}
        </Grid>
      </Grid>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    if (edit_attribute === "speed") {
      return renderSpeeds();
    } else if (edit_attribute === "senses") {
      return renderSenses();
    } else if (edit_attribute === "condition_immunities") {
      return renderConditionImmunities();
    } else if (edit_attribute === "immunities") {
      return renderDamageTypes(attributes, 0);
    } else if (edit_attribute === "resistances") {
      return renderDamageTypes(attributes, 0.5);
    } else if (edit_attribute === "vulnerabilities") {
      return renderDamageTypes(attributes, 2);
    } else if (edit_attribute === "tools") {
      return renderTools();
    } else if (edit_attribute === "skills") {
      return renderSkills();
    } else if (edit_attribute === "saving_throws") {
      return renderSavingThrows();
    } else if (edit_attribute === "languages") {
      return renderLanguages();
    } else {
      return (
        <FlexBox direction="column" style={{ cursor: "pointer" }}>
          <FlexBox onClick={() => { setEditAttribute("speed"); }}>
            <b>Inherited Speeds</b> { attributes.speed_string }
          </FlexBox>
          <FlexBox onClick={() => { setEditAttribute("senses"); }}>
            <b>Inherited Senses</b> { attributes.senses_string }
          </FlexBox>
          <FlexBox onClick={() => { setEditAttribute("immunities"); }}>
            <b>Inherited Damage Immunities</b> { attributes.immunities_string }
          </FlexBox>
          <FlexBox onClick={() => { setEditAttribute("resistances"); }}>
            <b>Inherited Damage Resistances</b> { attributes.resistances_string }
          </FlexBox>
          <FlexBox onClick={() => { setEditAttribute("vulnerabilities"); }}>
            <b>Inherited Damage Vulnerabilities</b> { attributes.vulnerabilities_string }
          </FlexBox>
          <FlexBox onClick={() => { setEditAttribute("condition_immunities"); }}>
            <b>Inherited Condition Immunities</b> { attributes.condition_immunities_string }
          </FlexBox>
          <FlexBox onClick={() => { setEditAttribute("tools"); }}>
            <b>Inherited Tools</b> { attributes.tools_string }
          </FlexBox>
          <FlexBox onClick={() => { setEditAttribute("skills"); }}>
            <b>Inherited Skills</b> { attributes.skills_string }
          </FlexBox>
          <FlexBox onClick={() => { setEditAttribute("saving_throws"); }}>
            <b>Inherited Saving Throws</b> { attributes.saving_throws_string }
          </FlexBox>
          <FlexBox onClick={() => { setEditAttribute("languages"); }}>
            <b>Inherited Languages</b> { attributes.languages }
          </FlexBox>
        </FlexBox>
      );
    }
  }
}
